// Badge camera decoding — one frame decoder, two backends.
//
// Field-proven 2026-07-21: the UROVO DT50S hardware trigger works great, but
// the camera path failed on a normal iPhone — iOS Safari ships NO
// `BarcodeDetector`. The original "native detector only, no wasm" trade was
// wrong: staff without a UROVO use their own phones, and half of those (every
// iPhone) can't decode without a wasm reader.
//
// So: prefer the native `BarcodeDetector` where it exists AND supports our
// symbologies (Android Chrome) — it's fast and adds no download — and fall back
// to zxing-wasm everywhere else. The wasm (~1 MB) is lazy: its reader JS is a
// dynamic import and the binary is only fetched when a device actually takes
// the zxing path and opens the camera. Devices with the native detector never
// pay for it.
//
// OFFLINE-FIRST: zxing-wasm defaults to fetching its binary from a jsDelivr CDN.
// We override `locateFile` to a Vite-bundled `?url` asset so it loads from our
// own origin and the service worker can cache it — no third-party fetch from
// the packhouse floor (same rule as the pdf.js worker in pdfDownscale.ts).

import { normaliseBadgeScan } from './workerBadge'
// Vite resolves `?url` to the emitted asset's URL string (cheap — the binary is
// only fetched when zxing instantiates). Mirrors pdfDownscale's worker import.
import zxingReaderWasmUrl from 'zxing-wasm/reader/zxing_reader.wasm?url'

/** Decode one video frame → canonical payload, or null if none this frame. */
export type BadgeFrameDecoder = (video: HTMLVideoElement) => Promise<string | null>

/** Maps a frame's decoded text payloads → one canonical payload (or null).
 *  Parameterising this is what makes the decoder generic (wave 2e): badges
 *  pass firstBadgeFromTexts, stock scanning passes its own prefix parser. */
export type TextNormaliser = (texts: readonly string[]) => string | null

/** Pure: first well-formed badge code among decoded text payloads. Testable. */
export function firstBadgeFromTexts(texts: readonly string[]): string | null {
  for (const t of texts) {
    const code = normaliseBadgeScan(t)
    if (code) return code
  }
  return null
}

// --- native BarcodeDetector (Android Chrome / Edge) -------------------------

interface DetectedBarcode { rawValue: string }
interface BarcodeDetectorLike { detect(source: CanvasImageSource): Promise<DetectedBarcode[]> }
type BarcodeDetectorCtor = (new (opts?: { formats?: string[] }) => BarcodeDetectorLike) & {
  getSupportedFormats?: () => Promise<string[]>
}

async function tryNativeDecoder(normalise: TextNormaliser): Promise<BadgeFrameDecoder | null> {
  const Ctor = (globalThis as unknown as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector
  if (typeof Ctor !== 'function') return null
  try {
    // A present-but-partial implementation (some list neither of ours) must
    // fall through to zxing rather than silently never matching.
    const supported = (await Ctor.getSupportedFormats?.()) ?? ['qr_code', 'code_128']
    const formats = ['qr_code', 'code_128'].filter(f => supported.includes(f))
    if (formats.length === 0) return null
    const detector = new Ctor({ formats })
    return async (video) => {
      try {
        return normalise((await detector.detect(video)).map(b => b.rawValue))
      } catch {
        return null   // transient unreadable frame (motion blur / mid-focus)
      }
    }
  } catch {
    return null
  }
}

// --- zxing-wasm fallback (iOS Safari, older browsers) -----------------------

let zxingConfigured = false

async function loadZxingReader() {
  const mod = await import('zxing-wasm/reader')
  if (!zxingConfigured) {
    mod.setZXingModuleOverrides({
      locateFile: (path: string, prefix: string) => (path.endsWith('.wasm') ? zxingReaderWasmUrl : prefix + path),
    })
    zxingConfigured = true
  }
  return mod
}

async function createZxingDecoder(normalise: TextNormaliser): Promise<BadgeFrameDecoder> {
  const mod = await loadZxingReader()
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) throw new Error('2D canvas unavailable')

  // wasm decode is far heavier than the native detector — cap it so a slow
  // phone stays responsive rather than decoding every animation frame.
  let lastDecodeAt = 0
  const MIN_GAP_MS = 220

  return async (video) => {
    const now = Date.now()
    if (now - lastDecodeAt < MIN_GAP_MS) return null
    lastDecodeAt = now

    const vw = video.videoWidth
    const vh = video.videoHeight
    if (!vw || !vh) return null

    // Downscale the long edge to ~640px: enough resolution for a badge held to
    // the camera, a fraction of the decode cost of a full 1080p frame.
    const scale = Math.min(1, 640 / Math.max(vw, vh))
    canvas.width = Math.round(vw * scale)
    canvas.height = Math.round(vh * scale)
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

    let results
    try {
      results = await mod.readBarcodesFromImageData(
        ctx.getImageData(0, 0, canvas.width, canvas.height),
        { formats: ['QRCode', 'Code128'], tryHarder: true, maxNumberOfSymbols: 1 },
      )
    } catch {
      return null
    }
    return normalise(results.filter(r => r.isValid).map(r => r.text))
  }
}

/**
 * Build the best available frame decoder for this device. Native where it can
 * read our symbologies, else zxing-wasm. Rejects only if neither can init
 * (e.g. wasm unreachable while fully offline before it was ever cached).
 */
export async function createFrameDecoder(normalise: TextNormaliser): Promise<BadgeFrameDecoder> {
  return (await tryNativeDecoder(normalise)) ?? (await createZxingDecoder(normalise))
}

export async function createBadgeFrameDecoder(): Promise<BadgeFrameDecoder> {
  return createFrameDecoder(firstBadgeFromTexts)
}
